import React, { useEffect, useState, useMemo } from "react";
import { AppLayout } from "/lib/component/partial";
import { Content } from "/lib/component/base";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TableSortLabel,
  TablePagination,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useRouter } from "../util";
import { parseStringParam } from "/lib/util/params";

type Order = "asc" | "desc";

interface Data {
  index: number;
  name: string;
  label: string;
  download: string;
}

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells: readonly HeadCell[] = [
    {id: "index", numeric: false, disablePadding: false, label: "Index"},
    {id: "name", numeric: false, disablePadding: false, label: "Report Name"},
    {id: "download", numeric: false, disablePadding: false, label: "Download"},
]

const EnhancedTableHead: React.FC<EnhancedTableProps> = ({...props}) => {
    const {onRequestSort, order, orderBy} = props;
    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
            <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
            >
                <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                >
                    {headCell.label}
                </TableSortLabel>
            </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export const ReportsView = () => {

  const baseURL = 'https://api.onemetrics-report-service.hardware-inno.gcpprodau.tbm.sh'

  const [page, setPage] = useState(0);
  const [error, setError] = useState(false);
  const [order, setOrder] = useState<Order>("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reportList, setReportList] = useState<Data[]>([]);
  const [orderBy, setOrderBy] = useState<keyof Data>("index");
  const router = useRouter()
  const namespace = parseStringParam(
    (router.match.params as any).namespace,
    ""
  )

  const useStyles = makeStyles({
    table: {
      backgroundColor: "#424242",
      fontFamily: "Arial",
    },
    tableCell: {
      color: "white",
    },
    printerTable: {
      height: "100%",
    },
  });
  const classes = useStyles();
  const theme = useTheme();

  useEffect(() => {
    const getLatestReports = async () => {
      try {
        const res = await fetch(`${baseURL}/report/list/${namespace}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await res.json();
        // console.log('fetch data from backend: ',data)
;
        const downloadAPI = `${baseURL}/report/download/`
        let output: Data[] = []
        for (let i = 0; i < data.length; i++) {
          let newData: Data = {
            index: i+1,
            name: data[i].split('/').pop(),
            label: "empty",
            download: `${downloadAPI}${data[i]}`
          }
          output.push(newData)
        }
        setReportList(output);
      } catch (error) {
        console.error("Fetch report error:", error);
        setError(true);
      }
    };
    getLatestReports();
  },[namespace]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const visibleRows = useMemo(() =>
    stableSort(
      reportList,
      getComparator(order, orderBy))
      .slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ), [reportList, order, orderBy, page, rowsPerPage])

  const handleDownload = async( downloadLink: string, name: string)=>{
    try{
      const res = await fetch(downloadLink,{
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      })
      if (!res.ok) {
        throw new Error("Network response was not ok")
      }
      const blob = await res.blob()
      const blobURL = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobURL
      link.download = name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobURL)
    }
    catch (error) {
      console.error("Fetch report and downloading file error:", error);
    }
  }

  return (
    <>
      <AppLayout>
        <Content marginBottom>
          <Box
            style={{
              backgroundColor: theme.palette.background.paper,
              marginTop: "40px",
              width: "100%",
              margin: "auto",
            }}>
            <TableContainer
              component={Paper}
              className={classes.table}
              style={{
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Table aria-label="simple table">
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={reportList.length}
                />
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.index}>
                      <TableCell className={theme.palette.text.primary} component="th" scope="row">
                        {row.index}
                      </TableCell>
                      <TableCell className={theme.palette.text.primary} align="left">
                        {row.name}
                      </TableCell>
                      <TableCell className={theme.palette.text.primary} align="left">
                        <Button variant="contained" color="primary" onClick={()=>handleDownload(row.download, row.name)}>
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
							component="div"
              rowsPerPageOptions={[5, 10, 25]}
              count={reportList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        </Content>
      </AppLayout>
    </>
  );
};

export default ReportsView;
